// src/constants/notifications.js
import api from "./api";

/** Kleiner Event-Bus für Sync zwischen Glocke (Badge) und Listen/UI */
class NotifBus {
  constructor() {
    this.target = new EventTarget();
  }
  on(type, handler) {
    this.target.addEventListener(type, handler);
    return () => this.target.removeEventListener(type, handler);
  }
  emit(type, detail) {
    this.target.dispatchEvent(new CustomEvent(type, { detail }));
  }
}

export const notifBus = new NotifBus();

/**
 * Link aus der DB (relativ, absolut oder extern) → Ziel für Router bzw. neues Tab.
 * @param {string|null|undefined} link
 * @returns {{ type: "router", to: string } | { type: "external", href: string } | null}
 */
export function notificationLinkTarget(link) {
  const s = String(link ?? "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      if (typeof window !== "undefined" && u.origin === window.location.origin) {
        return { type: "router", to: `${u.pathname}${u.search}${u.hash}` };
      }
      return { type: "external", href: s };
    } catch {
      return { type: "external", href: s };
    }
  }
  return { type: "router", to: s.startsWith("/") ? s : `/${s}` };
}

/** Kurzes UI-Label aus `type` (z. B. für Glocke / Liste). */
export function notificationTypeHint(type) {
  const t = String(type || "").toLowerCase();
  if (t.startsWith("service")) return "Service";
  if (t.startsWith("project")) return "Projekt";
  if (t.startsWith("offerte")) return "Offerte";
  if (t.startsWith("mangel")) return "Mängel";
  if (t.startsWith("pendenz")) return "Pendenz";
  if (t.includes("calendar") || t.includes("outlook")) return "Kalender";
  if (t.includes("inventar")) return "Inventar";
  if (t.includes("kunde") || t.includes("customer")) return "Kunde";
  return null;
}

/**
 * Kurzes Pill-Label nur für Prioritäten, die im UI extra hervorgehoben werden sollen.
 * `action` + `info` bewusst weglassen: Zuweisungen usw. sind schon am Titel / Kartenfarbton erkennbar —
 * ein durchgängiges „Aktion“-Badge wirkt redundant.
 * @param {string|null|undefined} priority DB-Feld `priority`
 * @returns {string|null}
 */
export function notificationPriorityBadgeLabel(priority) {
  const p = String(priority || "info").toLowerCase();
  if (p === "warning") return "Wichtig";
  if (p === "system") return "System";
  return null;
}

/** Stabile `types`-Arrays für `mark-related-read` (useEffect-Deps / Hook). */
export const RELATED_READ_TYPES = Object.freeze({
  service: Object.freeze([
    "service_assign",
    "service_status",
    "service_idle",
    "service_idle_summary",
  ]),
  project: Object.freeze(["project_assign", "project_status"]),
  offerte: Object.freeze(["offerte_assign", "offerte_status"]),
  mangel: Object.freeze(["mangel_assign", "mangel_status"]),
  pendenz: Object.freeze([
    "pendenz.assigned",
    "pendenz.reassigned",
    "pendenz.due_today",
    "pendenz.due_tomorrow",
  ]),
});

/* ---------------------------- API-Funktionen ---------------------------- */

export async function fetchUnreadCount() {
  try {
    const res = await api.get("/notifications/unread_count");
    const n = Number(res?.data?.unread ?? 0);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Optionen: { limit=30, cursor, onlyUnread=false } → { rows, nextCursor } */
export async function fetchNotifications(opts = {}) {
  const p = new URLSearchParams();
  if (opts.limit) p.set("limit", String(opts.limit));
  if (opts.cursor) p.set("cursor", String(opts.cursor));
  if (opts.onlyUnread) p.set("onlyUnread", "1");

  const qs = p.toString() ? `?${p.toString()}` : "";
  const res = await api.get(`/notifications${qs}`);
  const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
  const nextCursor = res?.data?.nextCursor ?? null;
  return { rows, nextCursor };
}

export async function readNotification(id) {
  await api.post(`/notifications/${id}/read`);
  notifBus.emit("changed", { kind: "read-one", id });
}

export async function readAllNotifications() {
  await api.post("/notifications/read-all");
  notifBus.emit("changed", { kind: "read-all" });
}

/** Detaillierte Zähler (Badge, Action, Warning, …) */
export async function fetchNotificationCounts() {
  try {
    const res = await api.get("/notifications/counts");
    const c = res?.data?.counts;
    if (!c || typeof c !== "object") return null;
    return {
      badge: Number(c.badge) || 0,
      action: Number(c.action) || 0,
      warning: Number(c.warning) || 0,
      infoUnread: Number(c.infoUnread) || 0,
      totalUnread: Number(c.totalUnread) || 0,
    };
  } catch {
    return null;
  }
}

/** Verknüpfte Notifications als gelesen (Detailseiten). */
export async function markRelatedNotificationsRead(body) {
  const res = await api.post("/notifications/mark-related-read", body, { silent: true });
  notifBus.emit("changed", { kind: "mark-related", updated: res?.data?.updated });
  return Number(res?.data?.updated ?? 0);
}

/* ----------- Helfer: optimistische Count-Anpassung im Badge ----------- */
export function calcNewCount(oldCount, kind, delta = 1) {
  if (kind === "read-all") return 0;
  if (kind === "read-one") return Math.max(0, (Number(oldCount) || 0) - (Number(delta) || 1));
  if (kind === "mark-related") return Math.max(0, (Number(oldCount) || 0) - (Number(delta) || 0));
  return oldCount || 0;
}
